import _ from 'lodash';
import React  from 'react';
import { useHistory } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import { JobMenuItem, moveJobToOperation, removeJobMenuItem, replaceJobMenuItem } from '../features/jobMenuSlice';
import { popScreenFromStack, replaceScreenUrlOnStack, ScreenStackState, ScreenStackItem, moveScreenToOperation, moveScreenToTop } from '../features/screenStackSlice';
import { changeOperation } from '../features/operationSlice';

import { getOperationKey, stringsEqualIgnoreCase } from '../util';
import { openNewPage, NavigationType } from '../util/navigationHelpers';
import useGlobalYesNoDialog from './useGlobalYesNoDialog';
import constants from '../constants';
import {getEpochFromDate} from '../dateUtils';
import fastStorage from "../fastStorage";
import { useStore } from 'react-redux';

function findScreenNames(jobMenuSection:Array<JobMenuItem>, urls:Array<string>, currentScreenUrl:string) {
  return jobMenuSection.filter(x =>
    !stringsEqualIgnoreCase(currentScreenUrl, x.url) &&
    urls.some(u => stringsEqualIgnoreCase(u, x.url))
  ).map(x => x.label);
}

interface IndexByCleanup {
  [key: string]: (() => void),
};
const CleanupMap:IndexByCleanup = {}; // Here we register cleanup for each screen

interface LastScreenQueryResult {
  item: ScreenStackItem|undefined,
  operation: string,
}
/**
 * When standalone operation screen is closed, pick last opened screen of all operations
 *
 * @param screenStack
 */
function getLastOpenedRegularScreenOnWholeStack(screenStack:ScreenStackState, currentUrl:string):LastScreenQueryResult {
  const allStack = _.orderBy([
    ...screenStack.oceanImport.map(x => ({item: x, operation: constants.operations['oceanImport'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.oceanExport.map(x => ({item: x, operation: constants.operations['oceanExport'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.airImport.map(x => ({item: x, operation: constants.operations['airImport'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.airExport.map(x => ({item: x, operation: constants.operations['airExport'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.otherShipment.map(x => ({item: x, operation: constants.operations['otherShipment'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.accounting.map(x => ({item: x, operation: constants.operations['accounting'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.warehousing.map(x => ({item: x, operation: constants.operations['warehousing'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.customer.map(x => ({item: x, operation: constants.operations['customer'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.standalone.map(x => ({item: x, operation: constants.operations['standalone'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.opReport.map(x => ({item: x, operation: constants.operations['opReport'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.whReceipt.map(x => ({item: x, operation: constants.operations['whReceipt'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.utility.map(x => ({item: x, operation: constants.operations['utility'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.billings.map(x => ({item: x, operation: constants.operations['billings'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.myEvent.map(x => ({item: x, operation: constants.operations['myEvent'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.businessIntell.map(x => ({item: x, operation: constants.operations['businessIntell'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.ams.map(x => ({item: x, operation: constants.operations['ams'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.isf.map(x => ({item: x, operation: constants.operations['isf'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.myTask.map(x => ({item: x, operation: constants.operations['myTask'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.shipmentStatus.map(x => ({item: x, operation: constants.operations['shipmentStatus'], epoch: getEpochFromDate(x.timestamp)})),
    ...screenStack.aiCenter.map(x => ({item: x, operation: constants.operations['aiCenter'], epoch: getEpochFromDate(x.timestamp)})),
  ], 'epoch', ['desc']);

  const last = allStack.find(x => x.item.url !== currentUrl);
  return {
    item: last ? last.item : undefined,
    operation: last?.operation ?? constants.operations['home'],
  };
}

export default function useJobMenus() {
  const history = useHistory();

  const dispatch = useAppDispatch();
  const operationState = useAppSelector(x => x.operation);
  const activeOperation = operationState.operation;
  const screenStack = useAppSelector(x => x.screenStack);
  const jobMenu = useAppSelector(x => x.jobMenu);
  const store = useStore();

  const [showGlobalDialog] = useGlobalYesNoDialog();

  function cleanupUrl(urlParam:string) {
    if (urlParam) {
      if (CleanupMap[urlParam]) {
        CleanupMap[urlParam]();
      }
      delete CleanupMap[urlParam];
      cleanupFastStorage(urlParam)
    }
  }

  function cleanupFastStorage(urlParam: string) {
    //const urlMatchToSearch = !urlParam.includes('common/joblistview') ? getRoutePath(urlParam) : urlParam;
    const fastStorageKeys = Object.keys(fastStorage);
    //const foundKeys = fastStorageKeys.filter(item => item.includes(urlMatchToSearch));
    const foundKeys = fastStorageKeys.filter(item => item.includes(urlParam));
    if(foundKeys.length) {
      foundKeys.forEach(foundKey => {
        delete fastStorage[foundKey];
      })
    }
  }

  const removeSingleJobMenu = (url: string, urlOperation: string) => {
    const operationToApplyChangesFor = !!urlOperation
      ? urlOperation
      : activeOperation;

    dispatch(removeJobMenuItem({
      url,
      activeOperation: getOperationKey(operationToApplyChangesFor)
    }));
    dispatch(popScreenFromStack({
      url,
      activeOperation: getOperationKey(operationToApplyChangesFor)
    }));

    cleanupUrl(url);
  }

  const removeSingleJobMenuinTab = (url: string, urlOperation: string) => {
    //edit,view로 열려있는 탭을 닫을때
    //Edit 버튼을 클릭하여 Edit으로 오픈했을때와 URL이 다른 경우 (ex: /oceanImport/mbl/edit/${id}/2) 
    //Edit으로 열린 탭이 지워지지 않는 문제가 있어 (ex: /oceanImport/mbl/edit/${id} 로 시작하는 URL인 경우 전부 지울 수 있도록 수정)

    //현재 지우려는 URL이 존재하는지 확인
    const operationToApplyChangesFor = !!urlOperation
      ? urlOperation
      : activeOperation;

    const operation = getOperationKey(operationToApplyChangesFor);
    if (!operation) {
      return false;
    }

    const storeScreenStack = store.getState().screenStack as ScreenStackState;
    const operationScreenStack = storeScreenStack ? storeScreenStack[operation] : screenStack[operation];

    let index = operationScreenStack.findIndex(x => x.url && x.url.toLowerCase().startsWith(url.toLowerCase()));

    if(index >= 0){
      var realUrl = operationScreenStack[index].url;

      dispatch(removeJobMenuItem({
        url: realUrl,
        activeOperation: getOperationKey(operationToApplyChangesFor)
      }));
      dispatch(popScreenFromStack({
        url: realUrl,
        activeOperation: getOperationKey(operationToApplyChangesFor)
      }));

      cleanupUrl(realUrl);
    }   
  }

  function hasSingleOpenedScreenUrl(urlOperation: string, queryUrl:string) {
    if (!queryUrl) {
      return false;
    }

    const operationToApplyChangesFor = !!urlOperation
      ? urlOperation
      : activeOperation;

    const operation = getOperationKey(operationToApplyChangesFor);
    if (!operation) {
      return false;
    }

    const storeScreenStack = store.getState().screenStack as ScreenStackState;
    const operationScreenStack = storeScreenStack ? storeScreenStack[operation] : screenStack[operation];

    return operationScreenStack.findIndex(x => x.url && x.url.toLowerCase() === queryUrl.toLowerCase()) >= 0;
  }

  function hasSingleOpenedScreenStartingWithUrl(urlOperation: string, queryUrl:string) {
    if (!queryUrl) {
      return false;
    }

    const operationToApplyChangesFor = !!urlOperation
      ? urlOperation
      : activeOperation;

    const operation = getOperationKey(operationToApplyChangesFor);
    if (!operation) {
      return false;
    }

    const storeScreenStack = store.getState().screenStack as ScreenStackState;
    const operationScreenStack = storeScreenStack ? storeScreenStack[operation] : screenStack[operation];

    return operationScreenStack.findIndex(x => x.url && x.url.toLowerCase().startsWith(queryUrl.toLowerCase())) >= 0;
  }

  
  function hasSingleOpenedScreenStartingWithUrlWithoutMe(urlOperation: string, queryUrl:string, ignoreUrl: string) {
    if (!queryUrl) {
      return false;
    }

    const operationToApplyChangesFor = !!urlOperation
      ? urlOperation
      : activeOperation;

    const operation = getOperationKey(operationToApplyChangesFor);
    if (!operation) {
      return false;
    }

    const storeScreenStack = store.getState().screenStack as ScreenStackState;
    const operationScreenStack = storeScreenStack ? storeScreenStack[operation] : screenStack[operation];

    return operationScreenStack.findIndex(x => x.url && x.url.toLowerCase().startsWith(queryUrl.toLowerCase()) && ignoreUrl.toLowerCase() !== x.url.toLowerCase()) >= 0;
  }


  function cleanupAll() {
    const storeScreenStack = store.getState().screenStack as ScreenStackState;

    const allJobMenuItemsToCloseOnLogout = [
      ...storeScreenStack.home,
      ...storeScreenStack.oceanImport,
      ...storeScreenStack.oceanExport,
      ...storeScreenStack.airImport,
      ...storeScreenStack.airExport,
      ...storeScreenStack.otherShipment,
      ...storeScreenStack.whReceipt,
      ...storeScreenStack.accounting,
      ...storeScreenStack.warehousing,
      ...storeScreenStack.opReport,
      ...storeScreenStack.customer,
      ...storeScreenStack.standalone,
      ...storeScreenStack.utility,
      ...storeScreenStack.billings,
      ...storeScreenStack.myEvent,
      ...storeScreenStack.businessIntell,
      ...storeScreenStack.ams,
      ...storeScreenStack.isf,
      ...storeScreenStack.myTask,
      ...storeScreenStack.shipmentStatus,
      ...storeScreenStack.aiCenter,
    ];

    allJobMenuItemsToCloseOnLogout.forEach(x => {
      dispatch(removeJobMenuItem({
        url: x.url,
        activeOperation: null
      }));
      dispatch(popScreenFromStack({
        url: x.url,
        activeOperation: null
      }));

      cleanupFastStorage(x.url);
    });

  }

  function hasSingleOpenedScreenInAnyOperationStartingWithUrl(queryUrl:string) {
    return !!queryUrl &&
      _.keys(screenStack).some(operation => screenStack[operation].findIndex(x => x.url && x.url.toLowerCase().startsWith(queryUrl.toLowerCase())) >= 0);
  }

  function hasSingleOpenedScreenInAnyOperationStartingWithUrlWithoutMe(queryUrl:string, ignoreUrl: string) {
    return !!queryUrl &&
      _.keys(screenStack).some(operation => screenStack[operation].findIndex(x => x.url 
        && x.url.toLowerCase().startsWith(queryUrl.toLowerCase()) 
        && !x.url.toLowerCase().startsWith(ignoreUrl.toLowerCase()) ) >= 0);
  }

  function getSingleOpenedScreenInAnyOperationStartingWithUrl(queryUrl: string) {
    const storeScreenStack = store.getState().screenStack as ScreenStackState;

    let filterOper = _.find(_.keys(storeScreenStack), (oper) => {
      return storeScreenStack[oper].find(s => s.url.toLowerCase().startsWith(queryUrl.toLowerCase())) ? true : false;
    }) ?? '';

    let lastScreen = undefined;
    if (filterOper) {
      lastScreen = storeScreenStack[filterOper].find(x => x.url.toLowerCase().startsWith(queryUrl.toLowerCase()));
    }

    return {
      item: lastScreen,
      operation: filterOper ?? 'home',
    };
  }

  
  function getOpenedScreenInAnyOperationContainWithUrl(queryUrls: string[], isMatch?: boolean) {
    const storeScreenStack = store.getState().screenStack as ScreenStackState;

    // make to all lowercase.
    queryUrls = queryUrls.map(x=>x.toLowerCase());

    // find opend operation.
    let filterOper = _.find(_.keys(storeScreenStack), (oper) => {
      return storeScreenStack[oper].find(s => queryUrls.some( q => q.length > s.url.length ?  q.indexOf(s.url.toLowerCase()) > -1 : s.url.toLowerCase().indexOf(q) > -1)) ? true : false;
    }) ?? '';

    // find opend view url.
    let lastScreen = undefined;
    if (filterOper) {
      if (isMatch) {
        lastScreen = storeScreenStack[filterOper].find(x => queryUrls.some( q => q === x.url));
      } else {
        lastScreen = storeScreenStack[filterOper].find(x => queryUrls.some( q => q.length > x.url.length ?  q.indexOf(x.url.toLowerCase()) > -1 : x.url.toLowerCase().indexOf(q) > -1));
      }
    } else {
      return null;
    }

    return {
      item: lastScreen,
      operation: filterOper,
    };
  }


  function moveToSingleOpenedScreenInAnyOperationStartingWithUrl(oepnRequestUrl: string, queryUrls: string[], needAfterScreenUrl?: boolean | undefined, isMatch?: boolean) {
    let parentOp = history.location.search.split("parentOp=")[1];
    //Invoice~CRDB > View모드로 열었다가 Detail에서 Edit으로 전환하는 경우 , Blop에서 열었으면 sidemenu가 blop가 되어야하고 billing에서 열었으면 billings가 되어야한다.
    if (parentOp === undefined && ['invoice', 'bill', 'crdb'].some(op => history.location.pathname.includes(op))) {
      if (!!activeOperation && activeOperation === 'Billings') parentOp = 'billings'
    }
    let requestEditView = oepnRequestUrl.indexOf('/edit') > -1;
    let opendeUrls = getOpenedScreenInAnyOperationContainWithUrl(queryUrls, isMatch);
    const addModeURLId = oepnRequestUrl.indexOf('/add') > -1 && parseInt(oepnRequestUrl.substring(oepnRequestUrl.indexOf('add/') + 4)) > 0;

    if(opendeUrls && !addModeURLId){
      // edit를 더 상위 권한으로 간주, 
      // view 화면이 열려있는데 edit 모드를 열려고 할 경우엔 view 모드 화면을 닫음.
      if(requestEditView && opendeUrls.item && opendeUrls.item.url.toLowerCase().indexOf('/view') > -1){
        removeSingleJobMenu(opendeUrls.item.url, opendeUrls.operation);
      }else{
        // edit 화면이 열려있는데 view 모드를 열겠다고 request 한 경우 열려있는 edit 화면을 다시 보여줌.
        oepnRequestUrl = opendeUrls.item?.url??oepnRequestUrl;
        parentOp = opendeUrls.operation;
      }

      // 이미 열려있는 화면을 다시 찾아서 show.
      dispatch(moveJobToOperation({
        sourceOperation: opendeUrls.operation,
        destinationOperation: parentOp??opendeUrls.operation,
        url: oepnRequestUrl,
      }));
      dispatch(moveScreenToOperation({
        sourceOperation: opendeUrls.operation,
        destinationOperation: parentOp??opendeUrls.operation,
        url: oepnRequestUrl,
      }));
      
      dispatch(moveScreenToTop({
        url: oepnRequestUrl,
        operation: parentOp??opendeUrls.operation,
      }));

      openNewPage(history, oepnRequestUrl, NavigationType.jobMenuSwitch, opendeUrls.item?.metadata, parentOp??opendeUrls.item?.afterScreenUrl);
      return;
    }

    // 열려있는 화면을 못 찾은 경우. 
    // 새로 open.
    if (needAfterScreenUrl) {
        openNewPage(history, oepnRequestUrl, NavigationType.newPage, null, 
            parentOp ? parentOp : ( history.location.pathname ?  history.location.pathname : null));
    } else {
      openNewPage(history, oepnRequestUrl, NavigationType.newPage, null, parentOp);
    }
  } 


  async function showSingleDependantScreenMessage(url: string, urlOperation: string, title:React.ReactNode|string|null|undefined) {
    const operationToApplyChangesFor = !!urlOperation
      ? urlOperation
      : activeOperation;

    const operation = getOperationKey(operationToApplyChangesFor);
    if (!operation) {
      return false;
    }
    const operationJobMenu = jobMenu[operation];
    if (!operationJobMenu) {
      return false;
    }

    const operationScreenStack = screenStack[operation];

    //  Build dependency tree of urls to delete
    const urlsToSkip = [];
    let query = [url];

    while (query && query.length > 0) {
      urlsToSkip.unshift(...query);
      // eslint-disable-next-line no-loop-func
      query = operationScreenStack.filter(s => query.some(q => stringsEqualIgnoreCase(q, s.parentUrl))).map(x => x.url);
    }

    if (urlsToSkip && urlsToSkip.length > 1) {
      // This screen has children on the stack. Show confirmation box and wait for confirmation
      const result = await showGlobalDialog(
        <div>
          <h3>Are you sure?</h3>

        {title}
        <br />
        <br />
        {findScreenNames(operationJobMenu, urlsToSkip, operationToApplyChangesFor).map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </div>
    );

      return result;
    }

    return true;
  }

  async function canGoBackSingle(urlOperation: string): Promise<boolean> {
    const operationToApplyChangesFor = !!urlOperation
      ? urlOperation
      : activeOperation;

    const operation = getOperationKey(operationToApplyChangesFor);
    if (!operation) {
      return true;
    }

    //  Build dependency tree of urls to delete
    const urlsToSkip:Array<string> = [];
    let query = [operationToApplyChangesFor];

    const operationScreenStack = screenStack[operation];

    while (query && query.length > 0) {
      urlsToSkip.unshift(...query);
      // eslint-disable-next-line no-loop-func
      query = operationScreenStack.filter(s => query.some(q => stringsEqualIgnoreCase(q, s.parentUrl))).map(x => x.url);
    }

    if (urlsToSkip && urlsToSkip.length > 1) {
      const operationJobMenu = jobMenu[operation];
      if (!operationJobMenu) {
        return true;
      }
      // This screen has children on the stack. Show confirmation box and wait for confirmation
      const doClose = await showGlobalDialog(
        <div>
          <h3>Are you sure?</h3>

        Are you sure you want to close the screen? There are depending windows open:
        <br />
        <br />
        {findScreenNames(operationJobMenu, urlsToSkip, operationToApplyChangesFor).map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </div>
    );

      if (!doClose) {
        return false;
      }
    }

    return true;
  }

  async function goBackSingle(url: string, urlOperation: string, cb:(() => void)|null = null, backScreenOverride:string|null = null):Promise<void> {
    removeSingleJobMenu(url, urlOperation);
    
    const operationToApplyChangesFor = !!urlOperation
      ? urlOperation
      : activeOperation;

    const operation = getOperationKey(operationToApplyChangesFor);
    if (!operation) {
      return;
    }

    const storeScreenStack = store.getState().screenStack as ScreenStackState;
    const operationScreenStack = storeScreenStack[operation];

    //  Build dependency tree of urls to delete
    const urlsToSkip:Array<string> = [];
    let query = [url];

    while (query && query.length > 0) {
      urlsToSkip.unshift(...query);
      // eslint-disable-next-line no-loop-func
      query = operationScreenStack.filter(s => query.some(q => stringsEqualIgnoreCase(q, s.parentUrl))).map(x => x.url);
    }

    let backScreenUrl:string|null = null;
    let backScreenMetadata = null;
    let backScreenAfterScreenUrl = null;
    let found = false;

    // In case of standalone operations it does not make sense to search for
    // back screen since it is always the last screen on the whole stack
    if (urlOperation !== constants.operations.standalone || urlOperation !== constants.operations.myTask) {
      _.forEachRight(operationScreenStack, x => {
        if (!found && urlsToSkip.every(u => !stringsEqualIgnoreCase(u, x.url))) {
          backScreenUrl = x.url;
          backScreenMetadata = x.metadata;
          backScreenAfterScreenUrl = x.afterScreenUrl;
          found = true;
        }
      });

      // If screen has parent defined, redirect to parent no matter the logic
      const currentScreenOnStack = operationScreenStack.find(x => stringsEqualIgnoreCase(x.url, url));
      if (currentScreenOnStack && currentScreenOnStack.parentUrl) {
        backScreenUrl = currentScreenOnStack.parentUrl;
        backScreenMetadata = currentScreenOnStack.metadata;
      }

      if (!backScreenUrl) {
        backScreenUrl = backScreenOverride;
      }
    }

    if (cb) {
      cb();
    }

    if (backScreenUrl) {
      urlsToSkip.forEach(x => cleanupUrl(x));
      openNewPage(history, backScreenUrl, NavigationType.jobMenuSwitch, backScreenMetadata, backScreenAfterScreenUrl);
    } else {
      // Common operations should always have backscreenUrl url. For standalone operations that is not the case
      // In that case we search last opened screen by timestamp nad switch to that operation and that screen
      const lastScreenQuery = getLastOpenedRegularScreenOnWholeStack(storeScreenStack, url);

      if (!lastScreenQuery || !lastScreenQuery.item) {
        dispatch(changeOperation('Home'));
        openNewPage(history, '/home/home', NavigationType.newPage);
      } else {
        dispatch(changeOperation(lastScreenQuery.operation));
        openNewPage(history, lastScreenQuery.item.url, NavigationType.jobMenuSwitch, lastScreenQuery.item.metadata, lastScreenQuery.item.afterScreenUrl);
      }
    }
  }

  function replaceSingleCurrentJobItemWith(newUrl:string, newLabel:any, oldLabel: string, oldUrl: string) {
    const locationState = history.location?.state as any;

    dispatch(replaceJobMenuItem({
      oldUrl,
      oldLabel,
      newUrl,
      newLabel,
      metadata: locationState?.metadata
    }));

    dispatch(replaceScreenUrlOnStack({
      oldUrl,
      newUrl,
      metadata: locationState?.metadata
    }));
  }

  function replaceSingleCurrentJobItemLabelWith(newLabel:string|null, oldLabel: string, url: string) {
    const locationState = history.location?.state as any;

    dispatch(replaceJobMenuItem({
      oldUrl: url,
      oldLabel,
      newUrl: url,
      newLabel,
      metadata: locationState?.metadata
    }));
  }

  function addSingleCleanupFunction(cleanupFnc: () => void, url: string) {
    CleanupMap[url] = cleanupFnc;
  }

  return {
    canGoBackSingle,
    goBackSingle,
    removeSingleJobMenu,
    showSingleDependantScreenMessage,
    hasSingleOpenedScreenUrl,
    hasSingleOpenedScreenStartingWithUrl,
    hasSingleOpenedScreenStartingWithUrlWithoutMe,
    hasSingleOpenedScreenInAnyOperationStartingWithUrl,
    hasSingleOpenedScreenInAnyOperationStartingWithUrlWithoutMe,
    getSingleOpenedScreenInAnyOperationStartingWithUrl,
    moveToSingleOpenedScreenInAnyOperationStartingWithUrl,
    replaceSingleCurrentJobItemWith,
    replaceSingleCurrentJobItemLabelWith,
    addSingleCleanupFunction,
    isFirstRun: (url: string) => !CleanupMap[url],
    cleanupFastStorage,
    cleanupAll,
    removeSingleJobMenuinTab
  };
}
